/* The read endpoint and token for these galleries — Triton staff only.
 *
 * A button rather than a panel, because this is setup information consulted once
 * when a site is wired up, not something anyone needs while arranging photos.
 * Leaving it on the page put a credential on screen during every screen-share.
 *
 * The role check here is presentation only. The endpoints behind it enforce the
 * same rule (see `adminOnly` in worker/gallerystudio.ts) — hiding a button is
 * not access control, and the token reads every gallery the client has.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { getGalleryApiUrl, rotateGalleryToken } from '@/data/client';
import { Modal } from './Modal';

function CopyRow({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [shown, setShown] = useState(!secret);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => {});
  };
  return (
    <>
      <span className="es-label">{label}</span>
      <div className="es-pickrow">
        <code className="es-apiurl">{shown ? value : '•'.repeat(Math.min(value.length, 40))}</code>
        {secret && (
          <button type="button" className="es-copy" onClick={() => setShown((v) => !v)}>
            {shown ? 'Hide' : 'Show'}
          </button>
        )}
        <button type="button" className="es-copy" onClick={copy} title="Copy">
          <Icon name={copied ? 'check-circle' : 'file-text'} size={14} /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </>
  );
}

function GalleryApiDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['galleryApiUrl'], queryFn: getGalleryApiUrl });
  const [err, setErr] = useState('');

  const rotate = useMutation({
    mutationFn: rotateGalleryToken,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['galleryApiUrl'] }),
    onError: (e) => setErr(e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : 'Could not rotate the token.'),
  });

  return (
    <Modal title="Gallery API" onClose={onClose}>
      <div className="es-modal__body">
        {isLoading && <p className="es-hint">Loading…</p>}
        {data && (
          <>
            <p className="es-hint" style={{ marginTop: 0 }}>
              Every gallery here, as JSON. Send the token as a bearer header — one gallery at a
              time with <code>/api/galleries/&lt;slug&gt;</code>.
            </p>
            <p className="es-hint">
              The same token lets a DNN site&rsquo;s TritonIQ Experts module sync this client&rsquo;s experts
              (<code>/api/experts-feed</code>).
            </p>
            <CopyRow label="Endpoint" value={data.url} />
            <CopyRow label="Token" value={data.token} secret />
            <span className="es-label">Example</span>
            <pre className="es-apicode">{`curl -H "Authorization: Bearer ${'•'.repeat(12)}" \\
  ${data.url}`}</pre>
            <span className="es-hint">
              Keep it in a build variable or a Worker secret — never in the repo, and never behind a
              <code>PUBLIC_</code> prefix, which would ship it to the browser.
            </span>
          </>
        )}
        {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
      </div>
      <div className="es-modal__foot">
        <Button variant="ghost" size="sm" disabled={rotate.isPending || !data}
          onClick={() => {
            if (confirm('Rotate the token?\n\nThe current one stops working immediately, and any site using it will stop loading galleries, and stop syncing experts, until it is updated.')) {
              setErr(''); rotate.mutate();
            }
          }}>
          {rotate.isPending ? 'Rotating…' : 'Rotate token'}
        </Button>
        <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}

export function GalleryApiCard() {
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState(false);
  if (!isAdmin) return null;

  return (
    <>
      <Button variant="ghost" size="sm" iconLeft={<Icon name="file-text" size={15} />} onClick={() => setOpen(true)}>
        Gallery API
      </Button>
      {open && <GalleryApiDialog onClose={() => setOpen(false)} />}
    </>
  );
}
